import { getRootContainerProps } from "@client/content/lib/helper";
import { makeButton } from "@client/content/modules/Button";
import { type ButtonContainerProps } from "@client/content/modules/Button/container";
import { type RootContainerProps } from "@client/content/types";
import { Alert } from "@client/controls/Alert/component";
import Icon from "@client/controls/Icon/component";
import TextInput from "@client/controls/Inputs/TextInput/component";
import { useDidUpdate } from "@client/lib/hooks";
import { TargetSelector } from "@client/selectors";
import { IconType } from "@client/types";
import { isStaffOrSuperUser } from "@common/appInfo";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import { type Checkout } from "../../types";
import CartItem from "../CartItem";
import OrderSubscriptions from "./Subscriptions";
import OrderTotals from "./Totals";

export type MapProps = RootContainerProps & {
  cart: Checkout.Cart;
  fieldStyle: React.CSSProperties;
  currencyCode: string;
  hasInvalidCoupon: boolean;
  isDiscountVisible: boolean;
  isEstimatedTax: boolean;
  isMobile: boolean;
  noImageUrl: string;
  paymentMethods: Checkout.PaymentMethod[];
  productVariantId: string | null;
  status: Checkout.Status;
};
export interface ActionProps {
  onApplyCoupon: (coupon: string) => void;
  onRemoveCoupon: (coupon: string) => void;
}
export type Props = ActionProps & MapProps;

const Button = makeButton<ButtonContainerProps>(TargetSelector.Entity, "coupon.button");
export default (props: Props) => {
  const i18n = useI18n();

  const [coupon, setCoupon] = React.useState("");

  React.useEffect(
    () => () => {
      props.status === "AdjustingOrderInfo" && setCoupon("");
    },
    [props.status, setCoupon]
  );

  const onApplyCoupon = React.useCallback(() => props.onApplyCoupon(coupon), [coupon, props.onApplyCoupon]);

  useDidUpdate(() => setCoupon(""), [props.cart.coupons]);

  return (
    <div className="order_summary">
      <h4>{i18n.orderSummary}</h4>

      <div className="items">
        {props.cart.items.map((x) => (
          <CartItem
            key={x.id}
            isMobile={props.isMobile}
            item={x}
            noImageUrl={props.noImageUrl}
            currencyCode={props.currencyCode}
          />
        ))}
      </div>

      {(props.cart.canApplyDiscountCodes || props.cart.canApplyVoucherCodes) && props.isDiscountVisible && (
        <div className="coupon_wrap">
          <table>
            <tbody>
              <tr>
                <td style={{ width: "99%" }}>
                  <TextInput
                    onChange={setCoupon}
                    placeholder={
                      props.cart.canApplyDiscountCodes && props.cart.canApplyVoucherCodes
                        ? i18n.infoEnterVoucherOrDiscountCode
                        : props.cart.canApplyDiscountCodes
                        ? i18n.infoEnterDiscountCode
                        : i18n.infoEnterVoucherCode
                    }
                    style={props.fieldStyle}
                    value={coupon}
                    maxLength={32}
                  />
                </td>
                <td>
                  <Button
                    {...getRootContainerProps(props)}
                    isDisabled={props.status === "AdjustingOrderInfo" || !coupon || props.cart.coupons.includes(coupon)}
                    onClick={onApplyCoupon}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <div className="applied">
                    {props.cart.coupons.map((x) => (
                      <div key={x} className="item">
                        {x} <Icon iconClassName={IconType.Cross} onClick={() => props.onRemoveCoupon(x)} />
                      </div>
                    ))}
                  </div>

                  {props.hasInvalidCoupon && <div className="error">{i18n.errorInvalidCoupon}</div>}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <OrderTotals
        doesRequireShipping={props.cart.requiresShipping}
        isEstimatedTax={props.isEstimatedTax}
        currencyCode={props.currencyCode}
        totals={props.cart}
        isConfirmation={false}
      />
      <OrderSubscriptions items={props.cart.items} currencyCode={props.currencyCode} />

      {isStaffOrSuperUser() && <Alert color={SystemColor.Orange}>{i18n.infoCheckoutEditMode}</Alert>}
    </div>
  );
};
